import { RfqPriority, SortOrder } from '@/dealers/_gen/gql'
import OrganizationM from '@/dealers/modules/Organization'
import ChartBreakdownLayout from '@/gf/components/Reports/ChartBreakdownLayout'
import DurationM from '@/gf/modules/Duration'
import { ReportDuration } from '@/gf/modules/ReportDuration'
import groupBy from 'lodash/groupBy'
import keyBy from 'lodash/keyBy'
import map from 'lodash/map'
import { DateTime, Duration } from 'luxon'
import { StringParam, useQueryParams, withDefault } from 'use-query-params'
import DurationInput from './DurationInput'
import BreakdownTable from './Service/BreakdownTable'
import Chart from './Service/Chart'
import HistoryTable from './Service/HistoryTable'
import TitleMetric from './Service/TitleMetric'
import { Context } from './Service/context'
import useQuery, { ComparisonRfq, Rfq } from './Service/useQuery'
import { useDuration } from './useDuration'

const median = (arr: number[]) => {
  const sorted = [...arr].sort((a, b) => a - b)
  const mid = Math.floor(sorted.length / 2)
  return sorted.length % 2 ? sorted[mid] : (sorted[mid - 1] + sorted[mid]) / 2
}

export type UpdateArgs = {
  fromDate?: DateTime | null
  toDate?: DateTime | null
  duration?: ReportDuration | null
  date?: DateTime | null
  orgId?: string | null
  userId?: string | null
  priority?: string | null
  breakdownBy?: string | null
  breakdownCustomerSort?: { field: string; order: SortOrder } | null
  breakdownUserSort?: { field: string; order: SortOrder } | null
  breakdownUrgencySort?: { field: string; order: SortOrder } | null
  rfqsSort?: { field: string; order: SortOrder } | null
  chartTab?: string | null
}

// Dealer-created quotes legitimately write `store_orders` ~10s before the matching
// `request_for_quote_stores` row, producing small negative deltas. Clamp at zero so
// those rows read as a near-instant response rather than polluting the median math.
export const rfqToTimeToQuote = (rfq: Rfq | ComparisonRfq) => {
  const diff = rfq.quote.insertedAt.diff(rfq.receivedAt)
  return diff.toMillis() < 0 ? Duration.fromMillis(0) : diff
}

export const rfqsToMedianTimeToQuoteMins = (rfqs: (Rfq | ComparisonRfq)[]) => {
  if (rfqs.length === 0) return null

  return Math.round(
    Duration.fromMillis(median(rfqs.map((rfq) => rfqToTimeToQuote(rfq).toMillis()))).as('minutes')
  )
}

export const formatTimeToQuote = (timeToQuote: Duration | null) =>
  timeToQuote && DurationM.format(timeToQuote)

export const formatTimeToQuoteMins = (minutes: number) =>
  DurationM.format(Duration.fromObject({ minutes }))

export const formatShortTimeToQuoteMins = (minutes: number) =>
  DurationM.formatShort(Duration.fromObject({ minutes }))

export const filterRfqsReceivedBetween = <T extends Pick<Rfq, 'receivedAt'>>(
  rfqs: T[],
  [start, end]: DateTime<true>[]
) => rfqs.filter((rfq) => start <= rfq.receivedAt && rfq.receivedAt <= end)

export const urgencyByPriority = {
  [RfqPriority.High]: 'High',
  [RfqPriority.Medium]: 'Medium',
  [RfqPriority.Low]: 'Low',
}

const useQueryParamsOpts = {
  date: StringParam,
  orgId: StringParam,
  userId: StringParam,
  priority: StringParam,
  'chart.tab': withDefault(StringParam, 'activity'),
  'breakdown.by': StringParam,
  'breakdown.user.sort': StringParam,
  'breakdown.customer.sort': StringParam,
  'breakdown.urgency.sort': StringParam,
  'rfqs.sort': StringParam,
}

const Service = () => {
  const [query, setQuery] = useQueryParams(useQueryParamsOpts)

  const {
    fromDate,
    toDate,
    prevToDate,
    prevFromDate,
    duration,
    dates,
    update: updateDuration,
  } = useDuration()

  const update = ({
    date,
    orgId,
    userId,
    priority,
    breakdownBy,
    breakdownCustomerSort,
    breakdownUserSort,
    breakdownUrgencySort,
    rfqsSort,
    chartTab,
    ...updates
  }: UpdateArgs) => {
    updateDuration(updates)

    if (date !== undefined) setQuery({ date: date ? date.toISODate() : undefined })
    if (orgId !== undefined) setQuery({ orgId: orgId || undefined })
    if (userId !== undefined) setQuery({ userId: userId || undefined })
    if (priority !== undefined) setQuery({ priority: priority || undefined })
    if (breakdownBy !== undefined) setQuery({ 'breakdown.by': breakdownBy || undefined })
    if (chartTab !== undefined) setQuery({ 'chart.tab': chartTab || undefined })

    if (breakdownCustomerSort !== undefined)
      setQuery({
        'breakdown.customer.sort': breakdownCustomerSort
          ? `${breakdownCustomerSort.field}.${breakdownCustomerSort.order.toLowerCase()}`
          : undefined,
      })

    if (breakdownUserSort !== undefined)
      setQuery({
        'breakdown.user.sort': breakdownUserSort
          ? `${breakdownUserSort.field}.${breakdownUserSort.order.toLowerCase()}`
          : undefined,
      })

    if (breakdownUrgencySort !== undefined)
      setQuery({
        'breakdown.urgency.sort': breakdownUrgencySort
          ? `${breakdownUrgencySort.field}.${breakdownUrgencySort.order.toLowerCase()}`
          : undefined,
      })

    if (rfqsSort !== undefined)
      setQuery({
        'rfqs.sort': rfqsSort ? `${rfqsSort.field}.${rfqsSort.order.toLowerCase()}` : undefined,
      })
  }

  const chartTab = query['chart.tab']
  const date = query.date ? DateTime.fromISO(query.date) : null
  const orgId = query.orgId || null
  const userId = query.userId || null
  const priority = query.priority || null
  const breakdownBy = query['breakdown.by'] || null
  const breakdownCustomerSort = query['breakdown.customer.sort']?.split('.') || null
  const breakdownUserSort = query['breakdown.user.sort']?.split('.') || null
  const breakdownUrgencySort = query['breakdown.urgency.sort']?.split('.') || null
  const rfqsSort = query['rfqs.sort']?.split('.') || null

  const [rfqsFilter, comparisonRfqsFilter] = [
    [fromDate, toDate],
    [prevFromDate, prevToDate],
  ].map(([d1, d2]) => JSON.stringify(['received_between', d1.toISODate(), d2.toISODate()]))

  const { rfqs, comparisonRfqs } = useQuery({
    variables: {
      rfqsSortBy: { field: 'received_at', order: SortOrder.Asc },
      rfqsFilter,
      comparisonRfqsFilter,
    },
  })

  const filteredRfqs = (() => {
    if (!rfqs) return undefined

    let result = rfqs
    result = orgId
      ? result.filter(({ quote }) => OrganizationM.orgKey(quote.customer.organization) === orgId)
      : result
    result = userId ? result.filter(({ quote }) => quote.creation?.creator.id === userId) : result
    result = priority ? result.filter((rfq) => rfq.priority === priority) : result
    return result
  })()

  const orgById =
    rfqs &&
    keyBy(
      rfqs.flatMap((rfq) => rfq.quote.customer.organization ?? []),
      (o) => o.id
    )

  const creatorById =
    rfqs &&
    keyBy(
      rfqs.flatMap((rfq) => (rfq.quote.creation ? [rfq.quote.creation.creator] : [])),
      (c) => c.id
    )

  // [ [ startDate, endDate ], ... ]
  const ranges = (() => {
    if (chartTab === 'trend' && dates.length > 27)
      return map(
        groupBy(dates, (d) => d.startOf('week', { useLocaleWeeks: true }).toISODate()),
        (_, isoDate) => {
          const start = DateTime.fromISO(isoDate) as DateTime<true>
          const end = start.endOf('week', { useLocaleWeeks: true })
          return [start, end]
        }
      )

    return dates.map((start, index) => {
      const end = dates.at(index + 1) || start
      return [start, end]
    })
  })()

  return (
    <Context.Provider
      value={{
        rfqs,
        comparisonRfqs,
        filteredRfqs,
        orgById,
        fromDate,
        toDate,
        duration,
        date,
        orgId,
        userId,
        priority,
        creatorById,
        breakdownBy,
        breakdownCustomerSort,
        breakdownUserSort,
        breakdownUrgencySort,
        rfqsSort,
        chartTab,
        ranges,
        update,
      }}
    >
      <ChartBreakdownLayout
        titleMetric={<TitleMetric />}
        durationInput={
          <DurationInput fromDate={fromDate} toDate={toDate} duration={duration} update={update} />
        }
        chart={<Chart />}
        breakdownTable={<BreakdownTable />}
        historyTable={<HistoryTable />}
      />
    </Context.Provider>
  )
}

export default Service
